import { convexQuery, useConvexMutation } from '@convex-dev/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '~/convex/_generated/api';
import type { Id } from '~/convex/_generated/dataModel';

/**
 * Wait 3 seconds after last text change before triggering analysis.
 * With Soniox endpoint detection enabled, tokens finalize at natural speech boundaries,
 * so this debounce aligns with natural pauses in speech.
 */
const DEBOUNCE_MS = 3000;

interface UseSectionGuidanceParams {
  reportId: Id<'reports'>;
  transcript: string;
  isRecording: boolean;
}

/**
 * Reactive hook for section guidance during recording.
 * Subscribes to sectionProgress from DB and triggers analysis via a lightweight mutation.
 * All LLM orchestration, retry logic, and status management lives on the backend.
 */
export const useSectionGuidance = ({
  reportId,
  transcript,
  isRecording,
}: UseSectionGuidanceParams) => {
  const [manualActiveIndex, setManualActiveIndex] = useState<number | null>(
    null,
  );

  // Subscribe to section progress (reactive — updates from DB automatically)
  const { data: sectionProgress } = useQuery(
    convexQuery(api.sectionProgress.getByReportId, { reportId }),
  );

  // Lightweight mutation to trigger analysis (no LLM call, just staleness check + schedule)
  const { mutate: triggerAnalysis } = useMutation({
    mutationFn: useConvexMutation(api.sectionProgress.triggerAnalysis),
  });

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastTriggeredTranscriptRef = useRef('');

  // Debounced trigger on transcript changes during recording
  useEffect(() => {
    if (!isRecording || transcript.length === 0) return;
    if (transcript === lastTriggeredTranscriptRef.current) return;

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    debounceTimerRef.current = setTimeout(() => {
      lastTriggeredTranscriptRef.current = transcript;
      setManualActiveIndex(null);
      triggerAnalysis({ reportId });
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [transcript, isRecording, reportId, triggerAnalysis]);

  // Clean up on recording stop
  useEffect(() => {
    if (!isRecording) {
      lastTriggeredTranscriptRef.current = '';
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    }
  }, [isRecording]);

  const setActiveSection = useCallback((index: number) => {
    setManualActiveIndex(index);
  }, []);

  // Manual override takes precedence until next LLM update clears it
  const activeSectionIndex =
    manualActiveIndex !== null
      ? manualActiveIndex
      : (sectionProgress?.activeSectionIndex ?? 0);

  return {
    activeSectionIndex,
    entries: sectionProgress?.entries ?? [],
    hint: sectionProgress?.hint ?? null,
    isAnalyzing:
      sectionProgress?.analysisStatus === 'running' ||
      sectionProgress?.analysisStatus === 'stale',
    setActiveSection,
  };
};
